/**
 * The Settings screen's General tab, against `/api/v1/settings/`.
 *
 * One path and one body shape, both verbs: `GET` reads the profile as it
 * stands, `PATCH` writes it and answers with the same record, so one mapping
 * serves both. This replaces the session store the tab used to read and write —
 * a profile now survives the tab it was edited in.
 *
 * Two records arrive under it. `practitioner_profile` is the person — their
 * name, age, time in practice and what they work on. `brand_profile` is the
 * practice they are reached at, and is the same record the My brand screen
 * edits, which is why the address and the phone numbers appear on both screens.
 * The form flattens the pair into one `DietitianProfile`; this file is where
 * that seam is.
 *
 * Two things about the save are worth knowing before changing it:
 *
 *   - It is `multipart/form-data`, not JSON. `api.patch` sends a `FormData` as
 *     it is and lets the browser set the boundary — see `client.ts`.
 *   - It sends only what changed. The endpoint is a partial update, so an
 *     omitted field is one left alone; `changedFields` diffs the draft against
 *     the last committed copy and builds the body from the difference. Which
 *     means a Settings save cannot overwrite the brand name or the email — this
 *     screen does not collect them, so they are never in the body.
 *
 * The picture travels as an id. The bytes went up ahead of the save, through
 * `fileService` with a purpose of `PROFILE_PHOTO`, so what goes here is the
 * `public_id` it answered with.
 */

import {
  ApiError,
  api,
  API_ENDPOINTS,
  withValidationDetail,
} from "../../../lib/api";
import {
  toInternationalNumber,
  toNationalNumber,
} from "../../../lib/fieldFormat";
import type { GetToken } from "../../auth/types/auth.types";
// The module rather than the feature's index: `mediaUrl` is about the API's
// origin, not about the brand screen, and reaching for it through `../../brand`
// would pull that whole screen in behind it.
import { mediaUrl } from "../../brand/services/fileService";
import { emptyProfile } from "../data/profileMock";
import {
  toGenderCode,
  toGenderLabel,
  toSpecializationCodes,
  toSpecializationLabels,
} from "../profileCodes";
import type {
  DietitianProfile,
  ProfilePhoto,
  SettingsFileResponse,
  SettingsResponse,
} from "../types/settings.types";

/** Months in a year, named because it is the whole of the experience mapping. */
const MONTHS_IN_YEAR = 12;

/** The file name the backend stored it under, for the card that lists it. */
const fileNameFrom = (url: string): string =>
  decodeURIComponent(url.split("/").pop() || "photo");

/**
 * A stored photo in the shape the form holds one.
 *
 * The local preview is reused where the response names the same file, which is
 * the ordinary case after a pick: the bytes are already in hand, and fetching
 * them back from the API's origin would be a request for something the browser
 * has. Only a photo this browser did not upload — one saved from another
 * device — falls back to the stored URL.
 */
const toProfilePhoto = (
  response: SettingsFileResponse | null | undefined,
  current: ProfilePhoto | null
): ProfilePhoto | null => {
  if (!response) return null;

  if (current && current.publicId === response.public_id) return current;

  return {
    name: fileNameFrom(response.url),
    // Absolute: the media lives on the API's origin, not the app's.
    dataUrl: mediaUrl(response.url),
    publicId: response.public_id,
    url: response.url,
  };
};

/**
 * The record as the form holds it.
 *
 * `current` is the profile the form already has, where there is one. It does
 * two jobs. It carries through the three things the wire does not hold — the
 * salutation the account was opened under, the Active pill, and the WhatsApp
 * checkbox — and it stands in for a half of the response that is missing
 * altogether: a body naming only the practitioner says nothing about the
 * practice, and reading that as "the address is now empty" would blank fields
 * the save never touched. On a load there is nothing to carry and `current` is
 * `null`, so a half that is not there is simply one nothing has been saved in.
 */
export const toDietitianProfile = (
  response: SettingsResponse,
  current: DietitianProfile | null
): DietitianProfile => {
  const base = current ?? emptyProfile;
  const practitioner = response.practitioner_profile ?? null;
  const brand = response.brand_profile ?? null;

  if (!practitioner && !brand) return base;

  const person: Pick<
    DietitianProfile,
    | "firstName"
    | "lastName"
    | "gender"
    | "age"
    | "photo"
    | "experienceYears"
    | "experienceMonths"
    | "specializations"
  > = practitioner
    ? {
        firstName: practitioner.first_name ?? "",
        lastName: practitioner.last_name ?? "",
        gender: toGenderLabel(practitioner.gender),
        age: practitioner.age == null ? "" : String(practitioner.age),
        photo: toProfilePhoto(practitioner.profile_photo, base.photo),
        // One figure on the wire, two in the form — because a length of service
        // is said as eight years and four months, not as a hundred months.
        experienceYears:
          practitioner.experience_months == null
            ? ""
            : String(Math.floor(practitioner.experience_months / MONTHS_IN_YEAR)),
        experienceMonths:
          practitioner.experience_months == null
            ? ""
            : String(practitioner.experience_months % MONTHS_IN_YEAR),
        specializations: toSpecializationLabels(practitioner.specializations),
      }
    : base;

  const phone = brand ? toNationalNumber(brand.phone_number ?? "") : base.phone;
  const whatsapp = brand
    ? toNationalNumber(brand.whatsapp_number ?? "")
    : base.whatsapp;

  return {
    // Not on the wire. Settled at sign-up, shown in the heading, and never
    // edited here — so it is kept rather than read back. Nor is `active`: a
    // practice with no record of being closed is open, which is what
    // `emptyProfile` says too.
    salutation: base.salutation,
    active: base.active,

    ...person,

    address: brand ? brand.address ?? "" : base.address,
    state: brand ? brand.state ?? "" : base.state,
    city: brand ? brand.city ?? "" : base.city,
    pincode: brand ? brand.postal_code ?? "" : base.pincode,
    phone,
    whatsapp,
    // The record holds two numbers, not the checkbox. Where the form has one,
    // it is carried through — a save must not untick the box under the cursor.
    // On a load it is read back off the numbers: stored equal, they were linked.
    whatsappSameAsPhone:
      current?.whatsappSameAsPhone ?? (whatsapp !== "" && whatsapp === phone),
  };
};

/** Time in practice as the one figure the backend keeps it in. */
const toExperienceMonths = (profile: DietitianProfile): number =>
  (Number(profile.experienceYears) || 0) * MONTHS_IN_YEAR +
  (Number(profile.experienceMonths) || 0);

/** One field of the body: a name and the text going up under it. */
type Field = [name: string, value: string];

/**
 * What changed, as the fields the `PATCH` should carry.
 *
 * Diffed rather than sent whole because the endpoint is a partial update and
 * this screen only collects part of the record: sending every field it knows
 * about would say nothing about the ones it does not, but sending an empty one
 * would clear a brand name the My brand screen had filled in.
 *
 * A field the user emptied still goes up, as an empty value — that is a change,
 * and the difference between "no age" and "leave the age alone" is the whole
 * point of diffing rather than pruning blanks.
 *
 * Exported for the suite: what this returns is the request, and asserting on it
 * is cheaper and clearer than parsing a multipart body back apart.
 */
export const changedFields = (
  next: DietitianProfile,
  saved: DietitianProfile
): Field[] => {
  const fields: Field[] = [];

  const changed = <K extends keyof DietitianProfile>(key: K) =>
    next[key] !== saved[key];

  if (changed("firstName")) fields.push(["first_name", next.firstName.trim()]);
  if (changed("lastName")) fields.push(["last_name", next.lastName.trim()]);
  if (changed("gender")) fields.push(["gender", toGenderCode(next.gender)]);
  if (changed("age")) fields.push(["age", next.age.trim()]);

  // Two controls, one field — so either of them moving sends the total.
  if (changed("experienceYears") || changed("experienceMonths")) {
    fields.push(["experience_months", String(toExperienceMonths(next))]);
  }

  // Replace-all, so it goes up as every code the profile should end with, one
  // repeated field per code. An emptied list is a single empty field: sending
  // nothing at all would leave the backend with the list the user just cleared.
  const specializations = toSpecializationCodes(next.specializations);
  if (
    JSON.stringify(specializations) !==
    JSON.stringify(toSpecializationCodes(saved.specializations))
  ) {
    if (specializations.length === 0) {
      fields.push(["specializations", ""]);
    } else {
      specializations.forEach((code) =>
        fields.push(["specializations", code])
      );
    }
  }

  // The id of an upload, or empty where the picture was taken off. Compared by
  // id rather than by object, so re-reading the same stored file is not a change.
  const photoId = next.photo?.publicId ?? "";
  if (photoId !== (saved.photo?.publicId ?? "")) {
    fields.push(["profile_photo", photoId]);
  }

  // The practice's half. The numbers go up in E.164, which is how the backend
  // keeps them and how they come back.
  if (changed("phone")) {
    fields.push(["phone_number", toInternationalNumber(next.phone)]);
  }
  if (changed("whatsapp")) {
    fields.push(["whatsapp_number", toInternationalNumber(next.whatsapp)]);
  }
  if (changed("address")) fields.push(["address", next.address.trim()]);
  if (changed("city")) fields.push(["city", next.city]);
  if (changed("state")) fields.push(["state", next.state]);
  if (changed("pincode")) fields.push(["postal_code", next.pincode]);

  return fields;
};

const toFormData = (fields: Field[]): FormData => {
  const body = new FormData();
  fields.forEach(([name, value]) => body.append(name, value));
  return body;
};

const sessionToken = async (getToken: GetToken, what: string) => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * The profile as it stands, or `null` for a workspace that has never filled one
 * in.
 *
 * A first visit is not a failure: a 404, or a body with neither record in it,
 * comes back as `null` and the screen opens on an empty profile. Every other
 * status still throws.
 */
export async function fetchDietitianProfile(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<DietitianProfile | null> {
  let response: SettingsResponse;

  try {
    response = await api.get<SettingsResponse>(API_ENDPOINTS.settings.profile, {
      token: await sessionToken(getToken, "read your profile"),
      signal,
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }

  if (!response?.practitioner_profile && !response?.brand_profile) return null;

  // Nothing was uploaded in this browser yet, so a stored photo arrives as its
  // URL and there is no local preview to keep.
  return toDietitianProfile(response, null);
}

/**
 * Saves what the form changed, and answers with the profile as the backend now
 * holds it.
 *
 * `saved` is the last committed copy, and is what `next` is diffed against —
 * pass the same copy the screen restores on Cancel, or the body will carry
 * fields nobody touched.
 *
 * A draft that differs only in what the wire does not carry — the Active pill,
 * the WhatsApp checkbox on its own — never reaches the network: there is
 * nothing to send, and `next` is already the answer.
 *
 * The response is what comes back, not what was posted: the backend normalises
 * as it stores (the numbers to `+91…`), so the form has to show what was
 * actually kept.
 */
export async function saveDietitianProfile(
  getToken: GetToken,
  next: DietitianProfile,
  saved: DietitianProfile,
  signal?: AbortSignal
): Promise<DietitianProfile> {
  const fields = changedFields(next, saved);
  if (fields.length === 0) return next;

  try {
    const response = await api.patch<SettingsResponse>(
      API_ENDPOINTS.settings.profile,
      toFormData(fields),
      { token: await sessionToken(getToken, "save your profile"), signal }
    );

    // The draft carries what the wire cannot: the salutation, the pill, the
    // checkbox, and the bytes of a picture already in hand.
    return toDietitianProfile(response ?? {}, next);
  } catch (error) {
    // A 400 names the fields it refused rather than saying "Validation failed".
    throw withValidationDetail(error);
  }
}
